import React from "react";
import { Mail, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import pic1 from "../../../assets/images/inbound calls 2.jpg";

const InboundCalls = () => {
  return (
    <div className="w-full" style={{ marginTop: "80px" }}>
      <motion.header
        className="bg-blue-900 text-white py-16 text-center shadow-lg rounded-lg"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="bg-gradient-to-r from-blue-50 to-indigo-100 py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="text-white">
                  📞 Enhance Patient Care with Expert{" "}
                </span>
                <span className="text-blue-600"> Inbound Call Solutions !</span>
              </h1>
              <p className="mt-4" style={{ fontWeight: "bold" }}>
                Focus on What Matters Most — Patient Care
              </p>
              <p className="text-white mb-8">
                At Right On Time Services, we ensure that your inbound call
                system operates smoothly, allowing your team to dedicate their
                time to what truly matters: delivering exceptional patient care.
              </p>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Inbound Call Management Section */}
      <section
        id="inbound-call-services"
        className="py-20 bg-gray-100 mt-4"
        style={{ padding: "30px" }}
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-blue-900 mb-8">
            🏥 Our Specialized Inbound Call Services
          </h2>
          <p className="text-base max-w-3xl mx-auto mb-12">
            Comprehensive Solutions for Your Practice’s Needs
          </p>
          <p className="text-base max-w-3xl mx-auto mb-12 mb-4">
            We offer a full suite of inbound call services, all tailored to meet
            the unique needs of your medical practice:
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-4">
            {/* Card 1 */}
            <div className="bg-white p-6 shadow-lg rounded-lg">
              <h3 className="text-xl font-semibold text-blue-900 mb-4">
                📅 Inbound Call Management
              </h3>
              <p className="text-sm text-gray-700 mb-4">
                Efficient call management is essential for healthcare providers.
                Our trained professionals ensure that every call is:
              </p>
              <ul className="list-disc text-sm text-gray-700 space-y-2">
                <ol>Answered promptly and professionally</ol>
                <ol>
                  Patient-focused to ensure your patients always feel valued
                </ol>
              </ul>
            </div>

            {/* Card 2 */}
            <div className="bg-white p-6 shadow-lg rounded-lg">
              <h3 className="text-xl font-semibold text-blue-900 mb-4">
                📆 Appointment Scheduling
              </h3>
              <p className="text-sm text-gray-700 mb-4">
                Let us handle appointment bookings, ensuring that your patients
                are scheduled smoothly while your medical staff can focus on
                providing quality care.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white p-6 shadow-lg rounded-lg">
              <h3 className="text-xl font-semibold text-blue-900 mb-4">
                💬 Patient Inquiries and Support
              </h3>
              <p className="text-sm text-gray-700 mb-4">
                We address patient questions and concerns with professionalism
                and care, fostering trust and building long-term relationships
                with your patients.
              </p>
            </div>

            {/* Card 4 */}
            <div className="bg-white p-6 shadow-lg rounded-lg">
              <h3 className="text-xl font-semibold text-blue-900 mb-4">
                💊 Prescription Refill Assistance
              </h3>
              <p className="text-sm text-gray-700 mb-4">
                Streamline prescription management with our prompt and accurate
                refill services, saving time for both your team and your
                patients.
              </p>
            </div>

            {/* Card 5 */}
            <div className="bg-white p-6 shadow-lg rounded-lg">
              <h3 className="text-xl font-semibold text-blue-900 mb-4">
                🌟 Exceptional Customer Service
              </h3>
              <p className="text-sm text-gray-700 mb-4">
                Our skilled representatives manage a variety of call scenarios
                with exceptional service, reflecting positively on your practice
                and building patient satisfaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us? */}

      <section className="py-16 bg-white" data-aos="fade-up">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {/* Left Side - Text Content */}
            <div className="flex flex-col justify-center">
              <h2 className="text-3xl font-bold text-blue-900 text-left">
                🌟 Why Choose Right On Time Services for Inbound Call Solutions?
              </h2>
              <ul className="list-disc list-inside text-lg mt-8 text-gray-800 space-y-4">
                <li>
                  <strong>Enhanced Patient Satisfaction:</strong> Prompt and
                  professional responses help foster trust and loyalty.
                </li>
                <li>
                  <strong>Increased Appointment Bookings:</strong> Informative
                  and friendly calls encourage patients to schedule necessary
                  visits.
                </li>
                <li>
                  <strong>Improved Patient Communication:</strong> We handle
                  follow-ups and inquiries efficiently, ensuring better care
                  delivery.
                </li>
                <li>
                  <strong>HIPAA Compliant:</strong> We adhere to the highest
                  standards of data security and confidentiality, providing
                  peace of mind for your practice and patients.
                </li>
              </ul>
            </div>

            {/* Right Side - Image */}
            <div className="flex items-stretch">
              <img
                src={pic1}
                alt="Inbound Call Solutions"
                className="w-full object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      <section id="inbound-call-benefits" className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-blue-900 mb-8">
            🌐 Benefits of a Reliable Inbound Call System
          </h2>
          <p className="text-base max-w-3xl mx-auto mb-8">
            Why Outsource Inbound Call Management?
          </p>
          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-gray-100 p-6 shadow-md rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Better Patient Engagement
              </h3>
              <p className="text-sm text-gray-700">
                Personalized responses help build lasting relationships.
              </p>
            </div>
            <div className="bg-gray-100 p-6 shadow-md rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Operational Efficiency
              </h3>
              <p className="text-sm text-gray-700">
                Your medical staff can focus on core services, while we handle
                communication.
              </p>
            </div>
            <div className="bg-gray-100 p-6 shadow-md rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Secure and Compliant Processes
              </h3>
              <p className="text-sm text-gray-700">
                We maintain HIPAA-compliant procedures, ensuring the protection
                of sensitive patient data.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="contact-inbound-call" className="py-20 bg-gray-50 mt-4 mb-4">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-blue-900 mb-4">
            🚀 Ready to Optimize Your Inbound Call System?
          </h2>
          <p className="text-base mb-6">
            Let’s Get Started – Together!
            <br />
            Contact Right On Time Services today and enhance your inbound call
            management.
          </p>
          <div className="text-sm text-gray-800 space-y-2">
            <p>
              📧 Email:{" "}
              <a
                href="mailto:info@rightontimeservices.com"
                className="text-blue-700 hover:underline"
              >
                info@rightontime.biz
              </a>
            </p>
            <p>
              📍 Address: Al-Rehman Chamber, Fazal e Haq Road, G 7/3 Blue Area,
              Islamabad, 44000 Rawalpindi.
            </p>
            <p>
              📞 Phone:{" "}
              <a
                href="tel:03394017907"
                className="text-blue-700 hover:underline"
              >
                03394017907
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Contact & Form Section */}

      {/* <section className="py-16 px-6 bg-white" data-aos="fade-up">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900">
            📅 Book a Free Consultation
          </h2>
          <p
            className="text-lg text-gray-700 mt-4"
            style={{ marginBottom: "12px" }}
          >
            Get Started Today:
          </p>
          <form className="mt-8 bg-gray-100 p-6 rounded-lg shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-700 font-semibold">
                  Select Service:
                </label>
                <select
                  className="w-full p-3 mt-2 rounded border"
                  style={{ padding: "6px" }}
                >
                  <option>BPO</option>
                  <option>Healthcare</option>
                  <option>Digital Marketing</option>
                  <option>Software Development</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="text-gray-700 font-semibold">
                  Full Name:
                </label>
                <input
                  type="text"
                  style={{ padding: "6px" }}
                  className="w-full p-3 mt-2 rounded border"
                  placeholder="Your Name"
                />
              </div>
              <div>
                <label className="text-gray-700 font-semibold">
                  Email Address:
                </label>
                <input
                  type="email"
                  style={{ padding: "6px" }}
                  className="w-full p-3 mt-2 rounded border"
                  placeholder="Your Email"
                />
              </div>
              <div>
                <label className="text-gray-700 font-semibold">
                  Phone Number:
                </label>
                <input
                  type="text"
                  style={{ padding: "6px" }}
                  className="w-full p-3 mt-2 rounded border"
                  placeholder="+92 - - - - - -"
                />
              </div>
              <div>
                <label className="text-gray-700 font-semibold">Subject:</label>
                <input
                  type="text"
                  style={{ padding: "6px" }}
                  className="w-full p-3 mt-2 rounded border"
                  placeholder="Your Subject"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="text-gray-700 font-semibold">
                Project Description:
              </label>
              <textarea
                style={{ padding: "6px" }}
                className="w-full p-3 mt-2 rounded border"
                rows="4"
              ></textarea>
            </div>
            <button
              className="w-full mt-6 bg-blue-700 py-3 font-bold rounded-full hover:bg-blue-900 transition"
              style={{ color: "white" }}
            >
              Submit Request
            </button>
          </form>
        </div>
      </section> */}
      <motion.div
        className="p-6 bg-white rounded-lg transition duration-300"
        data-aos="fade-up"
      >
        <section className="bg-gray-100  py-16 px-6 md:px-20">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              ✨ Choose Right On Time Services — Your Partner in Excellence
            </h2>
            <p className="text-lg text-gray-700 mb-6">
              Seamless Inbound Call Management. Enhanced Patient Satisfaction.
              Secure, Reliable, and Professional.
            </p>
          </div>
        </section>
      </motion.div>
    </div>
  );
};

export default InboundCalls;
